/**
 * AVA-765 — draws zone boundaries on the open 2D level plan (toggled from the viewer quick menu).
 * Each zone is an outline + light fill in its colour, labelled with its name.
 *
 * Boundaries are in global metres and go through the level's 2D↔3D mapping
 * (`mappingViewersPointsInfo._3dTo2d`), so they land exactly; they are only drawn on mapped levels
 * whose elevation range overlaps the zone's [zMin, zMax].
 */
import { addToScene, removeFromScene } from '@/utils/forgeUtils';

const THREE = window.THREE;
const Autodesk = window.Autodesk;
const SCENE = 'ava-zones-2d';
const LABEL_CLASS = 'ava-zone-plan-label';

function worldToPlanMapper(level, zone) {
  const mapping = level?.mappingViewersPointsInfo;
  if (!mapping?.isReady?.()) return null;
  const { zMin, zMax } = zone.boundary;
  // Skip zones that don't reach this level (when the level has a usable elevation range).
  if (level.topElevation > level.bottomElevation && (zMax < level.bottomElevation || zMin > level.topElevation)) return null;
  return (p) => {
    const point = mapping._3dTo2d(new THREE.Vector3(p.x, p.y, zMin));
    return point ? new THREE.Vector3(point.x, point.y, 0) : null;
  };
}

export class ZonePlanLayer {
  constructor(viewer) {
    this.viewer = viewer;
    this.meshes = [];
    this.labels = []; // { el, point }
    this.layout = this.layout.bind(this);
    viewer.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, this.layout);
  }

  /**
   * @param {object[]} zones - active zones
   * @param {object|null} level - the ForgeLevel shown in the plan viewer
   * @param {string|null} selectedZoneId - drawn stronger
   * @returns {number} how many zones were drawn
   */
  render(zones, level, selectedZoneId) {
    this.clear();
    if (!this.viewer.model) return 0;
    let drawn = 0;
    zones
      .filter((z) => z.boundary && z.status === 'active')
      .forEach((zone) => {
        const map = worldToPlanMapper(level, zone);
        const points = map && zone.boundary.polygon.map(map);
        if (!points || !points.every(Boolean)) return;
        this.drawZone(zone, points, zone.id === selectedZoneId);
        drawn++;
      });
    this.viewer.impl?.invalidate(false, false, true);
    this.layout();
    return drawn;
  }

  drawZone(zone, points, isSelected) {
    const colour = new THREE.Color(zone.colour);

    const shape = new THREE.Shape();
    shape.moveTo(points[0].x, points[0].y);
    points.slice(1).forEach((p) => shape.lineTo(p.x, p.y));
    const fill = new THREE.Mesh(
      new THREE.ShapeGeometry(shape),
      new THREE.MeshBasicMaterial({ color: colour, transparent: true, opacity: isSelected ? 0.3 : 0.14, depthTest: false, depthWrite: false }),
    );

    const edges = new THREE.Geometry();
    points.forEach((p, i) => edges.vertices.push(p, points[(i + 1) % points.length]));
    const outline = new THREE.Line(edges, new THREE.MeshBasicMaterial({ color: colour, depthTest: false, depthWrite: false }), THREE.LinePieces);

    [fill, outline].forEach((mesh) => {
      addToScene(mesh, this.viewer, SCENE);
      this.meshes.push(mesh);
    });

    const label = document.createElement('div');
    label.className = LABEL_CLASS;
    label.textContent = zone.name;
    Object.assign(label.style, {
      position: 'absolute',
      transform: 'translate(-50%, -50%)',
      background: zone.colour,
      color: '#ffffff',
      fontSize: '12px',
      fontWeight: '600',
      padding: '2px 8px',
      borderRadius: '10px',
      whiteSpace: 'nowrap',
      textShadow: '0 1px 2px rgba(0, 0, 0, 0.35)',
      boxShadow: isSelected ? '0 0 0 2px #ffffff' : 'none',
      pointerEvents: 'none',
      zIndex: 1,
    });
    this.viewer.container?.appendChild(label);
    this.labels.push({ el: label, point: new THREE.Box2().setFromPoints(points).getCenter() });
  }

  layout() {
    this.labels.forEach(({ el, point }) => {
      const screen = this.viewer.worldToClient(new THREE.Vector3(point.x, point.y, 0));
      el.style.left = `${screen.x}px`;
      el.style.top = `${screen.y}px`;
    });
  }

  clear() {
    removeFromScene(this.meshes, this.viewer, SCENE);
    this.meshes = [];
    this.labels.forEach(({ el }) => el.remove());
    this.labels = [];
    this.viewer.impl?.invalidate(false, false, true);
  }

  destroy() {
    this.clear();
    this.viewer.removeEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, this.layout);
  }
}
